import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Building2, MapPin, ArrowLeft, Plus, Layers,
    AlertTriangle, CheckCircle, Info
} from 'lucide-react';
import Layout from '../components/shared/Layout';
import ZoneCard from '../components/zones/ZoneCard';

interface Facility {
    id: string;
    name: string;
    address: string;
    totalArea: number;
    sectorType: 'TERTIARY' | 'INDUSTRIAL';
    riskLevel: 'STANDARD' | 'ELEVATED' | 'HIGH';
    createdAt: number;
    updatedAt: number;
}

interface Zone {
    id: string;
    zoneName: string;
    floorLevel: number;
    area: number;
    requiredUnits: number;
    createdAt: number;
}

export default function FacilityDetailsPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [facility, setFacility] = useState<Facility | null>(null);
    const [zones, setZones] = useState<Zone[]>([]);
    const [loading, setLoading] = useState(true);
    const [showZoneModal, setShowZoneModal] = useState(false);
    const [editingZone, setEditingZone] = useState<Zone | null>(null);

    // Zone Form State
    const [zoneForm, setZoneForm] = useState({
        zoneName: '',
        floorLevel: 0,
        area: '',
        requiredUnits: 0
    });

    useEffect(() => {
        if (id) {
            fetchFacilityDetails();
            fetchZones();
        }
    }, [id]);

    const fetchFacilityDetails = async () => {
        try {
            const response = await fetch(`https://fire-safety-api.tedguy280.workers.dev/api/facilities/${id}`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                },
            });
            const data = await response.json() as { success: boolean; facility: Facility };
            if (data.success) {
                setFacility(data.facility);
            }
        } catch (error) {
            console.error('Failed to fetch facility:', error);
        }
    };

    const fetchZones = async () => {
        try {
            const response = await fetch(`https://fire-safety-api.tedguy280.workers.dev/api/facilities/${id}/zones`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                },
            });
            const data = await response.json() as { success: boolean; zones: Zone[] };
            if (data.success) {
                setZones(data.zones);
            }
        } catch (error) {
            console.error('Failed to fetch zones:', error);
        } finally {
            setLoading(false);
        }
    };

    // Calculate required units automatically
    const calculateRequiredUnits = (area: number, sectorType: string) => {
        const divisor = sectorType === 'INDUSTRIAL' ? 150 : 200;
        return Math.ceil(area / divisor);
    };

    const handleAreaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const areaVal = e.target.value;
        const areaNum = parseFloat(areaVal);

        setZoneForm(prev => ({
            ...prev,
            area: areaVal,
            requiredUnits: !isNaN(areaNum) && facility
                ? calculateRequiredUnits(areaNum, facility.sectorType)
                : 0
        }));
    };

    const handleZoneSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const payload = {
            ...zoneForm,
            area: parseFloat(zoneForm.area), // Ensure number
        };

        try {
            const url = editingZone
                ? `https://fire-safety-api.tedguy280.workers.dev/api/zones/${editingZone.id}`
                : `https://fire-safety-api.tedguy280.workers.dev/api/facilities/${id}/zones`;

            const method = editingZone ? 'PUT' : 'POST';

            const response = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                },
                body: JSON.stringify(payload),
            });

            const data = await response.json() as { success: boolean };

            if (data.success) {
                await fetchZones();
                resetZoneForm();
            }
        } catch (error) {
            console.error('Failed to save zone:', error);
        }
    };

    const handleDeleteZone = async (zoneId: string) => {
        if (!window.confirm('Delete this zone?')) return;

        try {
            const response = await fetch(`https://fire-safety-api.tedguy280.workers.dev/api/zones/${zoneId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                },
            });
            if (response.ok) {
                fetchZones();
            }
        } catch (error) {
            console.error('Failed to delete zone:', error);
        }
    };

    const resetZoneForm = () => {
        setZoneForm({ zoneName: '', floorLevel: 0, area: '', requiredUnits: 0 });
        setEditingZone(null);
        setShowZoneModal(false);
    };

    const handleEditZone = (zone: Zone) => {
        setEditingZone(zone);
        setZoneForm({
            zoneName: zone.zoneName,
            floorLevel: zone.floorLevel,
            area: zone.area.toString(),
            requiredUnits: zone.requiredUnits
        });
        setShowZoneModal(true);
    };

    if (loading) return (
        <Layout>
            <div className="flex items-center justify-center min-h-screen">
                <div className="spinner"></div>
            </div>
        </Layout>
    );

    if (!facility) return (
        <Layout>
            <div className="p-8 text-center text-steel-400">Facility not found</div>
        </Layout>
    );

    const totalCoveredArea = zones.reduce((acc, z) => acc + z.area, 0);
    const coveragePercentage = Math.min((totalCoveredArea / facility.totalArea) * 100, 100);

    return (
        <Layout>
            <div className="min-h-screen p-8">
                {/* Header */}
                <div className="max-w-7xl mx-auto mb-8 animate-fadeIn">
                    <button
                        onClick={() => navigate('/facilities')}
                        className="flex items-center gap-2 text-steel-500 hover:text-white transition-colors mb-4 group"
                    >
                        <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                        Back to Facilities
                    </button>

                    <div className="flex justify-between items-start">
                        <div>
                            <h1 className="text-4xl font-display font-bold text-white mb-2 flex items-center gap-3">
                                <Building2 className="w-10 h-10 text-ember-500" />
                                {facility.name}
                            </h1>
                            <div className="flex items-center gap-4 text-steel-400">
                                <span className="flex items-center gap-1">
                                    <MapPin className="w-4 h-4" /> {facility.address}
                                </span>
                                <span className="px-2 py-0.5 rounded-full border border-steel-700 bg-steel-800/50 text-xs">
                                    {facility.sectorType}
                                </span>
                                <span className={`px-2 py-0.5 rounded-full border text-xs ${facility.riskLevel === 'HIGH' ? 'border-red-500/30 text-red-400 bg-red-500/10' :
                                    'border-safety-500/30 text-safety-400 bg-safety-500/10'
                                    }`}>
                                    {facility.riskLevel} Risk
                                </span>
                            </div>
                        </div>
                        <button
                            onClick={() => setShowZoneModal(true)}
                            className="btn btn-primary flex items-center gap-2"
                        >
                            <Plus className="w-5 h-5" /> Add Zone
                        </button>
                    </div>
                </div>

                {/* Coverage Stats */}
                <div className="max-w-7xl mx-auto mb-8 grid grid-cols-1 md:grid-cols-3 gap-6 animate-slideUp">
                    <div className="card-glass p-6">
                        <h3 className="text-steel-400 text-sm mb-1">Total Facility Area</h3>
                        <p className="text-2xl font-bold text-white">{facility.totalArea.toLocaleString()} m²</p>
                    </div>
                    <div className="card-glass p-6">
                        <h3 className="text-steel-400 text-sm mb-1">Covered Area (Zones)</h3>
                        <p className="text-2xl font-bold text-white">{totalCoveredArea.toLocaleString()} m²</p>
                        <div className="w-full h-1 bg-steel-800 rounded-full mt-2 overflow-hidden">
                            <div
                                className="h-full bg-safety-500 transition-all duration-1000"
                                style={{ width: `${coveragePercentage}%` }}
                            />
                        </div>
                    </div>
                    <div className="card-glass p-6">
                        <h3 className="text-steel-400 text-sm mb-1">Total Zones</h3>
                        <p className="text-2xl font-bold text-white">{zones.length}</p>
                    </div>
                </div>

                {/* Zone List */}
                <div className="max-w-7xl mx-auto">
                    <h2 className="text-xl font-display font-bold text-white mb-6 flex items-center gap-2">
                        <Layers className="w-5 h-5 text-ember-400" />
                        Defined Zones
                    </h2>

                    {zones.length === 0 ? (
                        <div className="card-glass p-12 text-center border-dashed border-2 border-steel-700">
                            <Layers className="w-12 h-12 text-steel-600 mx-auto mb-4" />
                            <p className="text-steel-400 mb-4">No zones defined yet.</p>
                            <button
                                onClick={() => setShowZoneModal(true)}
                                className="text-ember-400 hover:text-ember-300 font-medium"
                            >
                                Create your first zone
                            </button>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {zones.map(zone => (
                                <ZoneCard
                                    key={zone.id}
                                    zone={zone}
                                    onEdit={handleEditZone}
                                    onDelete={handleDeleteZone}
                                />
                            ))}
                        </div>
                    )}
                </div>

                {/* Add/Edit Modal */}
                {showZoneModal && (
                    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
                        <div className="card-glass max-w-lg w-full p-8 animate-scaleIn">
                            <h2 className="text-2xl font-display font-bold text-white mb-6">
                                {editingZone ? 'Edit Zone' : 'Add New Zone'}
                            </h2>
                            <form onSubmit={handleZoneSubmit} className="space-y-4">
                                <div>
                                    <label className="label">Zone Name</label>
                                    <input
                                        type="text"
                                        required
                                        className="input"
                                        value={zoneForm.zoneName}
                                        onChange={e => setZoneForm({ ...zoneForm, zoneName: e.target.value })}
                                        placeholder="e.g. Ground Floor Main Hall"
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="label">Floor Level</label>
                                        <input
                                            type="number"
                                            required
                                            className="input"
                                            value={zoneForm.floorLevel}
                                            onChange={e => setZoneForm({ ...zoneForm, floorLevel: parseInt(e.target.value) })}
                                        />
                                    </div>
                                    <div>
                                        <label className="label">Area (m²)</label>
                                        <input
                                            type="number"
                                            required
                                            min="1"
                                            step="0.01"
                                            className="input"
                                            value={zoneForm.area}
                                            onChange={handleAreaChange}
                                        />
                                    </div>
                                </div>

                                <div className="bg-steel-800/50 p-4 rounded-lg border border-steel-700/50">
                                    <div className="flex justify-between items-center mb-1">
                                        <span className="text-sm text-steel-400">Required Units (Calculated)</span>
                                        <div className="flex items-center gap-1 text-xs text-ember-400">
                                            <Info className="w-3 h-3" />
                                            Based on 1 unit / {facility.sectorType === 'INDUSTRIAL' ? '150' : '200'}m²
                                        </div>
                                    </div>
                                    <div className="text-3xl font-bold text-white">
                                        {zoneForm.requiredUnits}
                                    </div>
                                </div>

                                <div className="flex justify-end gap-3 pt-4">
                                    <button
                                        type="button"
                                        onClick={resetZoneForm}
                                        className="btn btn-secondary"
                                    >
                                        Cancel
                                    </button>
                                    <button type="submit" className="btn btn-primary">
                                        {editingZone ? 'Update Zone' : 'Save Zone'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
}
